import BreadCrumb from "Common/BreadCrumb";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Loadr from "Common/Components/Loader";
import { toast, ToastContainer } from "react-toastify";
import { createGame } from "services/apiGames";
import { useQueryClient } from "@tanstack/react-query";

const RADIO_LINK_BASE_URL = "https://www.betaplay.ng";

const CreateGame = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const validation = useFormik({
    initialValues: {
      title: "",
      description: "",
      amountPerSlot: "",
      type: "",
      radioSlug: "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Please Enter Title"),
      description: Yup.string()
        .max(255, "Description cannot exceed 255 characters")
        .required("Please Enter Description"),
      amountPerSlot: Yup.number()
        .positive("Amount must be a positive number")
        .required("Amount is required"),
      type: Yup.string().required("Please select a game type"),
      radioSlug: Yup.string().when("type", {
        is: "RADIO",
        then: (schema) =>
          schema
            .required("Radio link name is required (e.g. redss for /radiogames/redss)")
            .max(64, "Max 64 characters")
            .matches(/^[a-z0-9-]+$/, "Use only lowercase letters, numbers and hyphens"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const payload = {
          ...values,
          amountPerSlot: parseFloat(values.amountPerSlot),
        };
        if (values.type === "RADIO" && values.radioSlug) {
          payload.radioSlug = String(values.radioSlug).trim().toLowerCase().replace(/[^a-z0-9-]/g, "");
        } else if (values.type !== "RADIO") {
          payload.radioSlug = undefined;
        }
        const response = await createGame(payload);
        queryClient.invalidateQueries({ queryKey: ["games"] });
        const game = response?.data;
        if (game?.radioSlug) {
          const url = `${RADIO_LINK_BASE_URL}/radiogames/${game.radioSlug}`;
          navigator.clipboard.writeText(url).then(() => toast.info(`Radio link copied: ${url}`));
        }
        toast.success("Game created successfully");
        navigate("/games");
      } catch (error) {
        toast.error(error.message || "Failed to create game");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <React.Fragment>
      <BreadCrumb title="Create Game" pageTitle="Games" />
      <ToastContainer closeButton={false} limit={1} />
      <div className="card">
        <div className="card-body">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/games" className="text-slate-500 hover:text-custom-500">
              ← Back to Games
            </Link>
          </div>
          <h5 className="mb-4">Add new game</h5>
          <form onSubmit={validation.handleSubmit} className="grid grid-cols-1 gap-4 xl:grid-cols-12">
            <div className="xl:col-span-12">
              <label className="inline-block mb-2 text-base font-medium">Game Title</label>
              <input
                type="text"
                className="form-input border-slate-200 dark:border-zink-500 w-full"
                placeholder="Enter title"
                name="title"
                onChange={validation.handleChange}
                value={validation.values.title}
              />
              {validation.touched.title && validation.errors.title && (
                <p className="text-red-400">{validation.errors.title}</p>
              )}
            </div>
            <div className="xl:col-span-12">
              <label className="inline-block mb-2 text-base font-medium">Game Type</label>
              <select
                className="form-input border-slate-200 dark:border-zink-500 w-full"
                name="type"
                onChange={validation.handleChange}
                value={validation.values.type}
              >
                <option value="">Choose an option</option>
                <option value="LOTTERY">LOTTERY</option>
                <option value="RADIO">RADIO</option>
                <option value="TRIVIA">TRIVIA</option>
              </select>
              {validation.touched.type && validation.errors.type && (
                <p className="text-red-400">{validation.errors.type}</p>
              )}
            </div>
            {validation.values.type === "RADIO" && (
              <>
                <div className="xl:col-span-12">
                  <label className="inline-block mb-2 text-base font-medium">Radio link name</label>
                  <input
                    type="text"
                    className="form-input border-slate-200 dark:border-zink-500 w-full lowercase"
                    placeholder="e.g. redss (link: /radiogames/redss)"
                    name="radioSlug"
                    onChange={(e) => {
                      const v = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
                      validation.setFieldValue("radioSlug", v);
                    }}
                    value={validation.values.radioSlug}
                  />
                  <p className="mt-1 text-sm text-slate-500">Only letters, numbers and hyphens.</p>
                  {validation.touched.radioSlug && validation.errors.radioSlug && (
                    <p className="text-red-400">{validation.errors.radioSlug}</p>
                  )}
                </div>
                {validation.values.radioSlug && (
                  <div className="xl:col-span-12 p-3 rounded-lg bg-slate-100 dark:bg-zink-600 border border-slate-200 dark:border-zink-500">
                    <label className="inline-block mb-2 text-sm font-medium">Radio station link</label>
                    <div className="flex flex-wrap items-center gap-2">
                      <code className="text-sm text-custom-600 dark:text-custom-400 break-all">
                        {RADIO_LINK_BASE_URL}/radiogames/{validation.values.radioSlug}
                      </code>
                      <button
                        type="button"
                        className="btn btn-sm bg-custom-500 text-white"
                        onClick={() => {
                          navigator.clipboard.writeText(`${RADIO_LINK_BASE_URL}/radiogames/${validation.values.radioSlug}`);
                          toast.success("Link copied");
                        }}
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="xl:col-span-12">
              <label className="inline-block mb-2 text-base font-medium">Amount Per Ticket</label>
              <input
                type="number"
                className="form-input border-slate-200 dark:border-zink-500 w-full"
                placeholder="5000"
                name="amountPerSlot"
                onChange={validation.handleChange}
                value={validation.values.amountPerSlot}
              />
              {validation.touched.amountPerSlot && validation.errors.amountPerSlot && (
                <p className="text-red-400">{validation.errors.amountPerSlot}</p>
              )}
            </div>
            <div className="xl:col-span-12">
              <label className="inline-block mb-2 text-base font-medium">Description</label>
              <textarea
                rows={4}
                className="form-input border-slate-200 dark:border-zink-500 w-full"
                placeholder="Description"
                name="description"
                onChange={validation.handleChange}
                value={validation.values.description}
                maxLength={255}
              />
              {validation.touched.description && validation.errors.description && (
                <p className="text-red-400">{validation.errors.description}</p>
              )}
            </div>
            <div className="xl:col-span-12 flex gap-2">
              <Link to="/games" className="btn border-slate-200 dark:border-zink-500">
                Cancel
              </Link>
              <button
                type="submit"
                className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Creating..." : "Create Game"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CreateGame;
